import { type FC } from 'react';
import { Label, LabelVariants } from '@/components/ui/label';
import { type Asset } from '@/types/asset';
import { useTranslations } from 'next-intl';

export const CameraInformation: FC<{ asset: Asset; disabled: boolean }> = ({ asset, disabled }) => {
  const fileMetadata = asset?.fileMetadata;
  const tLabels = useTranslations('labels.camera');

  // List of EXIF keys we care about
  const keys = [
    'Exif.Photo.ExposureTime',
    'Exif.Photo.FNumber',
    'Exif.Photo.ISOSpeedRatings',
    'Exif.Photo.WhiteBalance',
    'Exif.Photo.Flash',
  ] as const;

  // Determine if any of the camera info values are present
  const hasCameraInfo = Boolean(fileMetadata && keys.some((key) => fileMetadata[key]?.value !== undefined));

  // If not, or fileMetadata is missing, or disabled is true, don't render the component
  if (!hasCameraInfo || !fileMetadata || disabled) {
    return null;
  }

  const format = (val?: string | number | boolean | Array<string> | Date | null) =>
    val === null || val === undefined ? '-' : val?.toString();

  return (
    <div className="space-y-3 bg-ui-light p-3">
      <p className="line-clamp-1 text-xs font-bold text-text-gray uppercase">{tLabels('information')}</p>
      <div className="space-y-3 text-lg leading-4 text-text-gray">
        <div className="flex justify-between">
          <div className="flex flex-col">
            <Label variant={LabelVariants.INSPECTOR_SECTION}>{tLabels('exposure')}</Label>
            {format(fileMetadata['Exif.Photo.ExposureTime']?.value)}
          </div>
          <div className="flex flex-col items-center">
            <Label variant={LabelVariants.INSPECTOR_SECTION}>{tLabels('aperture')}</Label>
            {format(fileMetadata['Exif.Photo.FNumber']?.value).replace(/^F(.+)$/, 'ƒ/$1')}
          </div>
          <div className="flex flex-col items-end">
            <Label variant={LabelVariants.INSPECTOR_SECTION}>ISO</Label>
            {format(fileMetadata['Exif.Photo.ISOSpeedRatings']?.value)}
          </div>
        </div>
        <div className="flex justify-between">
          <div className="flex flex-col">
            <Label variant={LabelVariants.INSPECTOR_SECTION}>{tLabels('whiteBalance')}</Label>
            {format(fileMetadata['Exif.Photo.WhiteBalance']?.value)}
          </div>
          <div className="flex flex-col items-end">
            <Label variant={LabelVariants.INSPECTOR_SECTION}>{tLabels('flash')}</Label>
            {format(fileMetadata['Exif.Photo.Flash']?.value)}
          </div>
        </div>
      </div>
    </div>
  );
};
